// Formes exactes de l'API Feedbaacks (server/internal/feedback/dto.go,
// comments.go, service.go, server/internal/api/*.go). Une seule source côté
// front : l'UI, le widget et le tableau de suivi importent d'ici.

export type FeedbackType = 'feature' | 'bug' | 'question'

export type FeedbackStatus =
  | 'open'
  | 'under_review'
  | 'planned'
  | 'in_progress'
  | 'on_hold'
  | 'shipped'
  | 'declined'
  | 'duplicate'

export const FEEDBACK_TYPES: readonly FeedbackType[] = ['feature', 'bug', 'question']

export const FEEDBACK_STATUSES: readonly FeedbackStatus[] = [
  'open', 'under_review', 'planned', 'in_progress', 'on_hold', 'shipped', 'declined', 'duplicate',
]

export type MemberRole = 'admin' | 'staff'

export interface Author {
  id: string
  name: string
  /** Réservé à l'équipe : absent pour les autres. */
  email?: string
  avatar_url?: string
}

export interface Media {
  id: string
  url: string
  mime_type: string
  size_bytes: number
  width?: number
  height?: number
  duration_seconds?: number
  created_at: string
}

export interface ConsoleError {
  ts: string
  message: string
}

export interface EditEntry {
  at: string
  by: string
  title: string
  body: string
  type: FeedbackType
  medias?: Media[]
}

/** Gestes ouverts au lecteur, calculés par le serveur (jamais côté client). */
export interface Permissions {
  can_edit: boolean
  can_change_status: boolean
  can_delete: boolean
  can_archive: boolean
  can_approve_agent: boolean
}

export interface FeedbackItem extends Permissions {
  id: string
  app: string
  type: FeedbackType
  status: FeedbackStatus
  title: string
  body: string
  author: Author
  is_public: boolean
  // Contexte technique : présent pour l'équipe seulement.
  page_url?: string
  viewport?: string
  user_agent?: string
  screenshot_url?: string
  console_errors?: ConsoleError[] | unknown
  medias: Media[]
  /** Contexte métier libre de l'app (ex. Academy : capsule, temps vidéo). */
  context?: Record<string, unknown>
  source: string
  edit_history?: EditEntry[]
  votes_up: number
  votes_down: number
  votes_score: number
  comments_count: number
  current_user_vote: 1 | -1 | null
  is_unread: boolean
  unread_comments_count: number
  duplicate_of?: string
  resolved_at?: string
  archived_at?: string
  is_archived: boolean
  agent_approved: boolean
  agent_approved_at?: string
  agent_approved_by?: string
  agent_claim_holder?: string
  agent_claim_expires_at?: string
  /** Qui traite le retour, montré à tous (l'auteur le suit) : ferme d'agents ou tour. */
  handled_by?: 'agent' | 'tower'
  /** Tour de contrôle : projet visé (tous), transmission (équipe). */
  target_project?: string
  tower_forwarded_at?: string
  created_at: string
  updated_at: string
}

/** Une personne citée dans un commentaire, et le texte qui la cite (« @Ana »). */
export interface Mention {
  user_id: string
  text: string
}

/** Un emoji, combien, et qui (dans l'ordre d'arrivée). */
export interface Reaction {
  emoji: string
  count: number
  users: string[]
}

export interface Comment {
  id: string
  item_id: string
  parent_id?: string
  author: Author
  body: string
  attachments: Media[]
  mentions: Mention[]
  reactions: Reaction[]
  reply_count: number
  edit_history?: Array<{ previous_text: string; edited_at: string; edited_by?: string }>
  source: string
  can_edit: boolean
  can_delete: boolean
  created_at: string
  updated_at: string
}

export interface SimilarItem {
  id: string
  type: FeedbackType
  status: FeedbackStatus
  title: string
  score: number
  created_at: string
  resolved_at?: string
}

export interface Counts {
  by_type: Record<FeedbackType, number>
  by_status_group: { open: number; in_progress: number; on_hold: number; shipped: number; declined: number }
  mine: number
  total: number
}

export interface VoteResult {
  votes_up: number
  votes_down: number
  votes_score: number
  current_user_vote: -1 | 0 | 1
}

export type Sort = 'score' | 'recent' | 'comments' | 'updated'
export type ArchivedView = 'active' | 'archived' | 'all'

export interface ListQuery {
  type?: FeedbackType[]
  status?: FeedbackStatus[]
  q?: string
  sort?: Sort
  mine?: boolean
  others?: boolean
  unread?: boolean
  /** Admin seulement (ignoré sinon par le serveur). */
  archived?: ArchivedView
  include_deleted?: boolean
  author_id?: string
  limit?: number
  offset?: number
}

export interface Page<T> {
  data: T[]
  has_more: boolean
}

export interface CreateInput {
  type: FeedbackType
  title?: string
  body?: string
  is_public?: boolean
  page_url?: string
  viewport?: string
  user_agent?: string
  screenshot_url?: string
  console_errors?: ConsoleError[]
  medias?: Media[]
  context?: Record<string, unknown>
}

export interface UpdateInput {
  title?: string
  body?: string
  type?: FeedbackType
  is_public?: boolean
  medias?: Media[]
  context?: Record<string, unknown>
}

export interface PublicApp {
  key: string
  name: string
  visibility: 'own' | 'all'
  types: FeedbackType[]
  allow_anonymous: boolean
}

export interface Session {
  token: string
  expires_at: string
  user: Author
  app: PublicApp
  role: MemberRole | ''
}

export interface Me {
  user: Author
  is_super_admin: boolean
  memberships: Array<{ app_key: string; role: MemberRole }>
  app: string
  notification_preferences: Record<string, unknown>
}

export interface AgentStatus {
  app_key?: string
  state: 'idle' | 'working' | 'blocked' | 'limited' | 'unknown'
  detail?: string | null
  feedback_id?: string | null
  queue_count: number
  limit_resets_at?: string | null
  next_tick_at?: string | null
  capacity_pct?: number | null
  capacity_detail?: string | null
  updated_at?: string
}

export interface Webhook {
  url: string
  secret: string
  events: string[]
}

/** Réglages d'une app (feedback.AppSettings). */
export interface AppSettings {
  visibility: 'own' | 'all'
  allow_anonymous: boolean
  types: FeedbackType[]
  author_delete_window_seconds: number
  item_url: string
  notifications: { email: boolean; monitor: string[] | null }
  agent: { trusted_authors: string[] | null; approvers: string[] | null; stale_hours: number }
  webhooks: Webhook[] | null
  extra?: Record<string, unknown>
}

export interface AppSummary {
  key: string
  name: string
  role: MemberRole
}

export interface AppDetail {
  key: string
  name: string
  allowed_origins: string[]
  publishable_key: string
  identity_secret: string
  settings: AppSettings
  created_at: string
  updated_at: string
}

export interface SecretKey {
  id: string
  app_key: string
  label: string
  created_at: string
  last_used_at?: string | null
  revoked_at?: string | null
}

export interface Member {
  app_key: string
  role: MemberRole
  created_at: string
  user_id: string
  email: string | null
  name: string
  avatar_url: string | null
}

/** File de validation des e-mails (super admins). Un e-mail retenu attend
 *  « pending » ; validé, il passe « sending » puis « sent » ou « failed ». */
export type EmailStatus = 'pending' | 'sending' | 'sent' | 'failed' | 'rejected'

export type EmailCounts = Record<EmailStatus, number>

export interface EmailSummary {
  id: string
  recipient: string
  subject: string
  /** 'digest', 'notification', ou une source ajoutée depuis. */
  source: string
  status: EmailStatus
  app_key: string | null
  created_at: string
  /** Absent pour un refus automatique (péremption). */
  reviewed_by: { id: string; name: string } | null
  reviewed_at: string | null
  reject_reason: string | null
  sent_at: string | null
  error_message: string | null
  attempts: number
}

export interface EmailDetail extends EmailSummary {
  body_html: string
  body_text: string
}

export interface EmailOutboxPage {
  data: EmailSummary[]
  counts: EmailCounts
  /** Faux : la porte est ouverte, les e-mails partent sans validation. */
  approval_required: boolean
}

/** Événement temps réel (realtime.Message côté serveur). Léger par
 *  construction : le client relit le retour avec ses propres droits. */
export interface RealtimeEvent {
  type: string
  app: string
  item_id: string
  data?: Record<string, unknown>
}

/** Une personne telle que son application la décrit. */
export interface Person {
  id: string
  name?: string
  email?: string
  avatar_url?: string
}

/** Un projet de la tour vers lequel un admin de l'app peut envoyer un retour. */
export interface TowerTarget {
  key: string
  label: string
}
